import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer, TileLayer, useMap, GeoJSON, CircleMarker, Popup, LayersControl, LayerGroup, Polyline, Marker, Tooltip,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Radar as RadarIcon, Play, Pause, Locate, RefreshCw, Loader2, AlertTriangle, Eye, EyeOff, X, ListFilter, ExternalLink, Navigation,
} from "lucide-react";

const RAINVIEWER_INDEX = "https://api.rainviewer.com/public/weather-maps.json";
const NWS_ALERTS = "https://api.weather.gov/alerts/active?status=actual&message_type=alert,update";
// RainViewer color scheme 6 = NEXRAD Level III (classic green/yellow/red, RadarScope-style)
const RADAR_COLOR = 6;
const FRAME_MS = 650;

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, zoom]);
  return null;
}

// --- Warning styling ---
const WARNING_STYLES = {
  tornado: { color: "#ef4444", weight: 2.5, fillColor: "#ef4444", fillOpacity: 0.25 },
  severe: { color: "#f59e0b", weight: 2, fillColor: "#f59e0b", fillOpacity: 0.2 },
  flood: { color: "#22c55e", weight: 2, fillColor: "#22c55e", fillOpacity: 0.2 },
  marine: { color: "#0ea5e9", weight: 2, fillColor: "#0ea5e9", fillOpacity: 0.18 },
  winter: { color: "#a78bfa", weight: 2, fillColor: "#a78bfa", fillOpacity: 0.2 },
  fire: { color: "#fb923c", weight: 2, fillColor: "#fb923c", fillOpacity: 0.2 },
  other: { color: "#facc15", weight: 1.5, fillColor: "#facc15", fillOpacity: 0.12 },
};

function classifyWarning(event = "") {
  const e = event.toLowerCase();
  if (e.includes("tornado")) return "tornado";
  if (e.includes("severe thunderstorm")) return "severe";
  if (e.includes("flood") || e.includes("flash flood")) return "flood";
  if (e.includes("marine") || e.includes("special marine") || e.includes("hurricane") || e.includes("tropical")) return "marine";
  if (e.includes("winter") || e.includes("ice") || e.includes("snow") || e.includes("blizzard")) return "winter";
  if (e.includes("fire") || e.includes("red flag")) return "fire";
  return "other";
}

const WARNING_LABELS = {
  tornado: "Tornado",
  severe: "Severe T-Storm",
  flood: "Flood",
  marine: "Marine / Tropical",
  winter: "Winter",
  fire: "Fire / Red Flag",
  other: "Other",
};

// Sort priority — most critical first
const WARNING_PRIORITY = ["tornado", "severe", "flood", "marine", "winter", "fire", "other"];

// --- LSR markers ---
const LSR_COLORS = {
  TORNADO: "#ef4444",
  "FUNNEL CLOUD": "#f97316",
  HAIL: "#06b6d4",
  "TSTM WND DMG": "#f59e0b",
  "TSTM WND GST": "#f59e0b",
  "HIGH WIND": "#f59e0b",
  "FLASH FLOOD": "#22c55e",
  "HEAVY RAIN": "#22c55e",
  "WALL CLOUD": "#a78bfa",
};
const lsrColor = (t) => LSR_COLORS[(t || "").toUpperCase()] || "#facc15";

function isoFmt(d) {
  return d.toISOString().replace(/\.\d+Z$/, "Z");
}
function isoNowMinusHours(h) {
  return isoFmt(new Date(Date.now() - h * 3600 * 1000));
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

// Centroid for flying to a warning polygon (rough)
function polygonCentroid(geometry) {
  if (!geometry) return null;
  let coords = [];
  if (geometry.type === "Polygon") coords = geometry.coordinates[0];
  else if (geometry.type === "MultiPolygon") coords = geometry.coordinates[0][0];
  if (!coords.length) return null;
  let lat = 0, lon = 0;
  for (const [x, y] of coords) { lon += x; lat += y; }
  return [lat / coords.length, lon / coords.length];
}

// --- Storm motion (parsed from NWS warning text, e.g. "moving northeast at 35 mph") ---
const MOTION_DIRS = {
  "north": 0, "north northeast": 22.5, "northeast": 45, "east northeast": 67.5,
  "east": 90, "east southeast": 112.5, "southeast": 135, "south southeast": 157.5,
  "south": 180, "south southwest": 202.5, "southwest": 225, "west southwest": 247.5,
  "west": 270, "west northwest": 292.5, "northwest": 315, "north northwest": 337.5,
  "n": 0, "nne": 22.5, "ne": 45, "ene": 67.5, "e": 90, "ese": 112.5, "se": 135, "sse": 157.5,
  "s": 180, "ssw": 202.5, "sw": 225, "wsw": 247.5, "w": 270, "wnw": 292.5, "nw": 315, "nnw": 337.5,
};

function parseStormMotion(desc) {
  if (!desc) return null;
  const m = desc.match(/moving\s+([a-z][a-z\s/-]*?)\s+at\s+(\d+)\s*mph/i);
  if (!m) return null;
  const dir = m[1].trim().replace(/-/g, " ").replace(/\s+/g, " ").toLowerCase();
  const bearing = MOTION_DIRS[dir];
  if (bearing === undefined) return null;
  return { bearing, speed: parseInt(m[2], 10), dir };
}

// Destination point given start, compass bearing (deg, clockwise from north), distance (miles)
function destPoint(lat, lon, bearingDeg, distMiles) {
  const R = 3958.8;
  const d = distMiles / R;
  const t = (bearingDeg * Math.PI) / 180;
  const p1 = (lat * Math.PI) / 180;
  const l1 = (lon * Math.PI) / 180;
  const p2 = Math.asin(Math.sin(p1) * Math.cos(d) + Math.cos(p1) * Math.sin(d) * Math.cos(t));
  const l2 = l1 + Math.atan2(Math.sin(t) * Math.sin(d) * Math.cos(p1), Math.cos(d) - Math.sin(p1) * Math.sin(p2));
  return [(p2 * 180) / Math.PI, (l2 * 180) / Math.PI];
}

// NWS storm attribute tags: max hail size (inches) + max wind gust
function getStormTags(props = {}) {
  const params = props.parameters || {};
  const hailRaw = params.maxHailSize?.[0];
  const windRaw = params.maxWindGust?.[0];
  const hail = hailRaw && !isNaN(parseFloat(hailRaw)) ? parseFloat(hailRaw).toFixed(2) : null;
  const wind = windRaw ? String(windRaw).toUpperCase().replace(/\s*MPH/, "") : null;
  if (!hail && !wind) return null;
  return { hail, wind: wind ? `${wind}` : null };
}

export default function RadarPage() {
  const [center] = useState([39.5, -98.35]);
  const [zoom] = useState(5);
  const [flyTarget, setFlyTarget] = useState(null);

  // Layer controls
  const [showReports, setShowReports] = useState(true);
  const [showTracks, setShowTracks] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  // Filter inside panel
  const [filter, setFilter] = useState("all");

  // Data
  const [warnings, setWarnings] = useState([]);
  const [reports, setReports] = useState([]);
  const [loadingWarnings, setLoadingWarnings] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // RainViewer animation — stacked frames, autoplaying
  const [radarHost, setRadarHost] = useState("https://tilecache.rainviewer.com");
  const [frames, setFrames] = useState([]);
  const [frameIdx, setFrameIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const intervalRef = useRef(null);

  // ---- Fetchers ----
  const fetchWarnings = async () => {
    setLoadingWarnings(true);
    try {
      const r = await fetch(NWS_ALERTS, { headers: { Accept: "application/geo+json" } });
      const json = await r.json();
      const feats = (json?.features || []).filter(
        (f) => f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon"),
      );
      setWarnings(feats);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (e) {
      // ignore
    } finally {
      setLoadingWarnings(false);
    }
  };

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const sts = isoNowMinusHours(12);
      const ets = isoFmt(new Date());
      const url = `https://mesonet.agron.iastate.edu/geojson/lsr.geojson?sts=${encodeURIComponent(sts)}&ets=${encodeURIComponent(ets)}`;
      const r = await fetch(url);
      const json = await r.json();
      const feats = (json?.features || []).filter((f) => f.geometry?.type === "Point");
      setReports(feats);
    } catch (e) {
      // ignore
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchFrames = async () => {
    try {
      const r = await fetch(RAINVIEWER_INDEX);
      const json = await r.json();
      if (json?.host) setRadarHost(json.host);
      const past = json?.radar?.past || [];
      const nowcast = json?.radar?.nowcast || [];
      const all = [...past, ...nowcast.map((f) => ({ ...f, forecast: true }))];
      setFrames(all);
      setFrameIdx(past.length - 1); // land on the latest real scan
    } catch {/* ignore */}
  };

  useEffect(() => {
    fetchWarnings();
    fetchReports();
    fetchFrames();
    const w = setInterval(fetchWarnings, 60_000);
    const lsr = setInterval(fetchReports, 5 * 60_000);
    const f = setInterval(fetchFrames, 5 * 60_000);
    return () => { clearInterval(w); clearInterval(lsr); clearInterval(f); };
  }, []);

  // Animation player
  useEffect(() => {
    if (playing && frames.length > 1) {
      intervalRef.current = setInterval(() => {
        setFrameIdx((i) => (i + 1) % frames.length);
      }, FRAME_MS);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [playing, frames]);

  const goToMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setFlyTarget([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { timeout: 8000 }
    );
  };

  // Sorted warnings for panel
  const sortedWarnings = useMemo(() => {
    const filtered = filter === "all" ? warnings : warnings.filter((w) => classifyWarning(w.properties?.event) === filter);
    return [...filtered].sort((a, b) => {
      const pa = WARNING_PRIORITY.indexOf(classifyWarning(a.properties?.event));
      const pb = WARNING_PRIORITY.indexOf(classifyWarning(b.properties?.event));
      if (pa !== pb) return pa - pb;
      const ea = new Date(a.properties?.expires || 0).getTime();
      const eb = new Date(b.properties?.expires || 0).getTime();
      return ea - eb; // soonest expiring first within same class
    });
  }, [warnings, filter]);

  const warningCounts = useMemo(() => {
    const counts = {};
    for (const f of warnings) {
      const key = classifyWarning(f.properties?.event);
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [warnings]);

  // Storm-motion tracks: 30-minute projected path for each moving warned cell
  const tracks = useMemo(() => {
    return warnings
      .map((w) => {
        const motion = parseStormMotion(w.properties?.description || "");
        const start = polygonCentroid(w.geometry);
        if (!motion || !start) return null;
        const dist = Math.min(Math.max(motion.speed * 0.5, 10), 60); // 30-min path, clamped for readability
        const end = destPoint(start[0], start[1], motion.bearing, dist);
        return { feature: w, motion, start, end };
      })
      .filter(Boolean);
  }, [warnings]);

  // Hail/wind attribute badges anchored at each warning polygon centroid
  const tags = useMemo(() => {
    return warnings
      .map((w) => {
        const storm = getStormTags(w.properties);
        const c = polygonCentroid(w.geometry);
        if (!storm || !c) return null;
        return { feature: w, storm, at: [c[0] - 0.1, c[1]] };
      })
      .filter(Boolean);
  }, [warnings]);

  const reportCounts = useMemo(() => {
    const counts = {};
    for (const f of reports) {
      const t = (f.properties?.typetext || f.properties?.event || "OTHER").toUpperCase();
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [reports]);

  const warningStyle = (feat) => WARNING_STYLES[classifyWarning(feat.properties?.event)];
  const onEachWarning = (feat, lyr) => {
    const p = feat.properties || {};
    const storm = getStormTags(p);
    const tagLine = storm
      ? `<div style="margin-top:8px;display:flex;gap:6px;font:700 11px 'Share Tech Mono',monospace;letter-spacing:.1em">
           ${storm.hail ? `<span style="border:1px solid #00F0FF;color:#00F0FF;padding:2px 6px">HAIL ${storm.hail}"</span>` : ""}
           ${storm.wind ? `<span style="border:1px solid #FF9900;color:#FF9900;padding:2px 6px">WIND ${storm.wind} MPH</span>` : ""}
         </div>`
      : "";
    const html = `
      <div style="font-family:'Manrope',sans-serif;color:#fff;min-width:240px">
        <div style="font:700 11px/1 'JetBrains Mono',monospace;letter-spacing:.18em;color:${WARNING_STYLES[classifyWarning(p.event)].color};text-transform:uppercase">${p.event || "Warning"}</div>
        <div style="font:800 16px/1.2 'Outfit',sans-serif;margin:8px 0 6px">${p.headline || p.senderName || "Active alert"}</div>
        <div style="font-size:12px;color:#a1a1aa;margin-bottom:6px">${p.areaDesc || ""}</div>
        <div style="font-size:11px;color:#71717a;font-family:'JetBrains Mono',monospace">
          Severity: <span style="color:#facc15">${p.severity || "—"}</span> · Certainty: ${p.certainty || "—"}<br/>
          Expires: ${p.expires ? new Date(p.expires).toLocaleString() : "—"}
        </div>
        ${tagLine}
      </div>`;
    lyr.bindPopup(html, { maxWidth: 360, className: "vg-popup" });
  };

  const focusWarning = (feat) => {
    const c = polygonCentroid(feat.geometry);
    if (c) setFlyTarget(c);
  };

  return (
    <div className="grain" data-testid="radar-page">
      {/* Header */}
      <section className="pt-32 pb-8">
        <div className="container-vg">
          <div className="overline">Live Field Radar</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter mt-4">
            NEXRAD <span className="text-yellow-400">base reflectivity.</span>
          </h1>
          <p className="text-zinc-400 mt-5 max-w-2xl text-lg leading-relaxed">
            Smooth, looping NEXRAD reflectivity with live NWS warnings always overlaid. Scrub the timeline or hit play,
            open <b>Warnings</b> to fly to any active alert, and read the last 12 hours of ground-truth storm reports.
          </p>
        </div>
      </section>

      {/* Toolbar + Map */}
      <section>
        <div className="container-vg">
          <div className="card-vg p-0 overflow-hidden relative">
            <div className="flex flex-wrap items-center gap-3 p-4 border-b border-white/10 bg-[#0d0f12]">
              <button
                onClick={() => setPanelOpen(true)}
                className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/15 flex items-center gap-2"
                data-testid="radar-open-warnings-list"
              >
                <AlertTriangle className="w-3 h-3" /> Warnings ({warnings.length})
                <ListFilter className="w-3 h-3 ml-1 opacity-70" />
              </button>

              <button
                onClick={() => setShowReports((s) => !s)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border flex items-center gap-2 ${
                  showReports ? "border-yellow-400/50 text-yellow-400 bg-yellow-400/5" : "border-white/10 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400"
                }`}
                data-testid="radar-toggle-reports"
              >
                {showReports ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Storm Reports ({reports.length})
              </button>

              <button
                onClick={() => setShowTracks((s) => !s)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border flex items-center gap-2 ${
                  showTracks ? "border-emerald-400/50 text-emerald-400 bg-emerald-400/5" : "border-white/10 text-zinc-400 hover:border-emerald-400 hover:text-emerald-400"
                }`}
                data-testid="radar-toggle-tracks"
              >
                {showTracks ? <Navigation className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Storm Tracks ({tracks.length})
              </button>

              <span className="hidden md:inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-sky-400 border border-sky-400/30 bg-sky-400/5 px-3 py-2" data-testid="radar-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" /> Animated · NEXRAD
              </span>

              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => { fetchWarnings(); fetchReports(); fetchFrames(); }}
                  className="text-xs font-mono uppercase tracking-wider border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400 px-3 py-2 inline-flex items-center gap-2"
                  data-testid="radar-refresh"
                >
                  {loadingWarnings || loadingReports ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Refresh
                </button>
                <button
                  onClick={goToMyLocation}
                  className="text-xs font-mono uppercase tracking-wider border border-white/10 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400 px-3 py-2 inline-flex items-center gap-2"
                  data-testid="radar-locate"
                >
                  <Locate className="w-3 h-3" /> My location
                </button>
              </div>
            </div>

            {/* Map + Side panel */}
            <div className="relative" style={{ height: "680px" }}>
              <MapContainer
                center={center}
                zoom={zoom}
                scrollWheelZoom
                style={{ height: "100%", width: "100%", background: "#0A0A0A" }}
              >
                <FlyTo center={flyTarget} zoom={flyTarget ? 8 : null} />

                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Dark">
                    <LayerGroup>
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                        attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                        maxZoom={16}
                      />
                      {/* Place/county labels above the radar, RadarScope-style */}
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                        zIndex={100}
                        maxZoom={16}
                      />
                    </LayerGroup>
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Streets">
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap'
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>

                {/* Animated NEXRAD radar — all frames stacked & preloaded, active one visible */}
                {frames.map((f, i) => (
                  <TileLayer
                    key={f.path}
                    url={`${radarHost}${f.path}/256/{z}/{x}/{y}/${RADAR_COLOR}/1_1.png`}
                    opacity={i === frameIdx ? 0.85 : 0}
                    zIndex={10 + i}
                    attribution='Radar: RainViewer'
                  />
                ))}

                {/* Warnings — always shown */}
                {warnings.length > 0 && (
                  <GeoJSON
                    key={`warnings-${warnings.length}-${lastUpdate}`}
                    data={{ type: "FeatureCollection", features: warnings }}
                    style={warningStyle}
                    onEachFeature={onEachWarning}
                  />
                )}

                {/* Hail / wind attribute badges on warning polygons */}
                {tags.map((t, i) => {
                  const cls = classifyWarning(t.feature.properties?.event);
                  const color = WARNING_STYLES[cls].color;
                  const parts = [];
                  if (t.storm.hail) parts.push(`<span class="vg-tag vg-tag-hail">HAIL ${t.storm.hail}&quot;</span>`);
                  if (t.storm.wind) parts.push(`<span class="vg-tag vg-tag-wind">WIND ${t.storm.wind} MPH</span>`);
                  return (
                    <Marker
                      key={`tag-${i}`}
                      position={t.at}
                      interactive={false}
                      icon={L.divIcon({
                        className: "vg-tag-wrap",
                        html: `<div class="vg-tag-stack" style="--tag-color:${color}">${parts.join("")}</div>`,
                        iconSize: [0, 0],
                        iconAnchor: [0, 0],
                      })}
                    />
                  );
                })}

                {/* Storm-motion track arrows (30-min projected path) */}
                {showTracks && tracks.map((t, i) => {
                  const cls = classifyWarning(t.feature.properties?.event);
                  const color = WARNING_STYLES[cls].color;
                  return (
                    <React.Fragment key={`track-${i}`}>
                      <Polyline
                        positions={[t.start, t.end]}
                        pathOptions={{ color, weight: 3, dashArray: "1 10", lineCap: "round", opacity: 0.95 }}
                      />
                      <Marker
                        position={t.end}
                        icon={L.divIcon({
                          className: "",
                          html: `<div data-track-arrow style="transform: rotate(${t.motion.bearing}deg); color:${color}; font-size:22px; line-height:1; text-shadow:0 0 8px rgba(0,0,0,.9)">▲</div>`,
                          iconSize: [22, 22],
                          iconAnchor: [11, 11],
                        })}
                      >
                        <Tooltip direction="top" offset={[0, -10]} className="vg-tooltip">
                          <span>
                            {`${t.feature.properties?.event || "Storm"} — moving ${t.motion.dir.toUpperCase()} at ${t.motion.speed} mph (30-min projected path)`}
                          </span>
                        </Tooltip>
                      </Marker>
                    </React.Fragment>
                  );
                })}

                {/* Storm reports */}
                {showReports && reports.map((f, i) => {
                  const [lon, lat] = f.geometry.coordinates;
                  const p = f.properties || {};
                  const t = (p.typetext || p.event || "REPORT").toUpperCase();
                  const c = lsrColor(t);
                  const mag = p.magf || p.magnitude || p.mag || "";
                  const remark = p.remark || p.comments || "";
                  return (
                    <CircleMarker
                      key={`lsr-${i}`}
                      center={[lat, lon]}
                      radius={5}
                      pathOptions={{ color: c, fillColor: c, fillOpacity: 0.9, weight: 1 }}
                    >
                      <Popup className="vg-popup" maxWidth={320}>
                        <div style={{ color: "#fff", minWidth: 200 }}>
                          <div style={{ font: "700 11px/1 'JetBrains Mono',monospace", letterSpacing: ".18em", color: c, textTransform: "uppercase" }}>{t}</div>
                          <div style={{ font: "800 15px/1.2 'Outfit',sans-serif", margin: "8px 0 4px" }}>
                            {mag ? `${mag} ${t.includes("HAIL") ? "in" : t.includes("WND") || t.includes("WIND") ? "mph" : ""}` : t}
                          </div>
                          <div style={{ fontSize: 12, color: "#a1a1aa" }}>
                            {(p.city || p.county || "Unknown")}, {p.st || p.state || ""}
                          </div>
                          <div style={{ fontSize: 11, color: "#71717a", fontFamily: "'JetBrains Mono',monospace", marginTop: 4 }}>
                            {fmtTime(p.utc_valid || p.valid)} · src: {p.source || p.wfo || "NWS"}
                          </div>
                          {remark && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,.12)", fontSize: 12, color: "#d4d4d8" }}>
                              {remark}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>

              {/* Warnings side panel (overlays map) */}
              {panelOpen && (
                <div
                  className="absolute inset-y-0 right-0 w-full sm:w-[420px] bg-[#0d0f12] border-l border-white/10 shadow-2xl flex flex-col z-[500]"
                  data-testid="warnings-panel"
                >
                  <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div>
                      <div className="overline !text-red-400">Active Warnings</div>
                      <div className="font-heading text-xl font-bold mt-1">{warnings.length} alerts · CONUS</div>
                    </div>
                    <button
                      onClick={() => setPanelOpen(false)}
                      className="text-zinc-400 hover:text-white"
                      data-testid="warnings-panel-close"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Filter chips */}
                  <div className="p-3 border-b border-white/10 flex flex-wrap gap-2" data-testid="warnings-filter">
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")} color="#facc15">
                      All ({warnings.length})
                    </FilterChip>
                    {WARNING_PRIORITY.map((k) => (
                      (warningCounts[k] || 0) > 0 && (
                        <FilterChip
                          key={k}
                          active={filter === k}
                          onClick={() => setFilter(k)}
                          color={WARNING_STYLES[k].color}
                          dataTestid={`warnings-filter-${k}`}
                        >
                          {WARNING_LABELS[k]} ({warningCounts[k]})
                        </FilterChip>
                      )
                    ))}
                  </div>

                  {/* List */}
                  <div className="flex-1 overflow-y-auto divide-y divide-white/5" data-testid="warnings-list">
                    {sortedWarnings.length === 0 ? (
                      <div className="p-8 text-center text-zinc-500 text-sm font-mono uppercase tracking-wider">
                        No warnings match this filter
                      </div>
                    ) : (
                      sortedWarnings.map((w, i) => {
                        const p = w.properties || {};
                        const cls = classifyWarning(p.event);
                        const color = WARNING_STYLES[cls].color;
                        return (
                          <button
                            key={p.id || i}
                            onClick={() => focusWarning(w)}
                            className="w-full text-left p-4 hover:bg-white/[0.03] focus:bg-white/[0.05] focus:outline-none"
                            data-testid={`warning-item-${i}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5" style={{ background: `${color}1A`, color, border: `1px solid ${color}66` }}>
                                {p.event || "Alert"}
                              </span>
                              {p.severity && (
                                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                  {p.severity}
                                </span>
                              )}
                            </div>
                            <div className="font-heading text-sm font-semibold mt-2 text-white leading-tight">
                              {p.headline || p.senderName || "Active alert"}
                            </div>
                            {(() => {
                              const storm = getStormTags(p);
                              if (!storm) return null;
                              return (
                                <div className="flex gap-2 mt-2" data-testid={`warning-tags-${i}`}>
                                  {storm.hail && (
                                    <span className="text-[10px] font-mono tracking-wider border border-cyan-400 text-cyan-300 px-2 py-0.5">
                                      HAIL {storm.hail}&quot;
                                    </span>
                                  )}
                                  {storm.wind && (
                                    <span className="text-[10px] font-mono tracking-wider border border-amber-500 text-amber-400 px-2 py-0.5">
                                      WIND {storm.wind} MPH
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                            <div className="text-xs text-zinc-400 mt-1 line-clamp-2">
                              {p.areaDesc || ""}
                            </div>
                            <div className="flex items-center justify-between mt-2 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                              <span>Expires {p.expires ? new Date(p.expires).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                              <span className="inline-flex items-center gap-1 text-yellow-400">
                                Locate on map <ExternalLink className="w-3 h-3" />
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Animation timeline — always on when frames are loaded */}
            {frames.length > 1 && (
              <div className="border-t border-white/10 p-4 bg-[#0d0f12] flex flex-wrap items-center gap-4" data-testid="radar-timeline">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="bg-yellow-400 text-black px-3 py-2 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider hover:bg-yellow-300"
                  data-testid="radar-play"
                >
                  {playing ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Play</>}
                </button>
                <input
                  type="range"
                  min={0}
                  max={frames.length - 1}
                  value={frameIdx}
                  onChange={(e) => { setPlaying(false); setFrameIdx(Number(e.target.value)); }}
                  className="flex-1 accent-yellow-400"
                  data-testid="radar-slider"
                />
                <div className="font-mono text-xs text-zinc-400 tabular-nums flex items-center gap-2" data-testid="radar-frame-time">
                  {frames[frameIdx]?.forecast ? (
                    <span className="text-purple-400 uppercase tracking-wider">Fcst</span>
                  ) : frameIdx === frames.filter((f) => !f.forecast).length - 1 ? (
                    <span className="inline-flex items-center gap-1 text-red-400 uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
                    </span>
                  ) : null}
                  <span>
                    {frames[frameIdx]?.time
                      ? new Date(frames[frameIdx].time * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </span>
                  <span className="text-zinc-600">{frameIdx + 1}/{frames.length}</span>
                </div>
              </div>
            )}
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="card-vg p-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <div className="overline !text-red-400">Active Warnings · CONUS</div>
              </div>
              <div className="font-heading text-4xl font-black mt-3 font-mono">{warnings.length}</div>
              <ul className="mt-4 space-y-1 text-xs font-mono">
                {Object.keys(WARNING_LABELS).map((k) => (
                  <li key={k} className="flex items-center gap-3" data-testid={`warning-count-${k}`}>
                    <span className="w-2 h-2" style={{ background: WARNING_STYLES[k].color }} />
                    <span className="text-zinc-400 flex-1 uppercase tracking-wider">{WARNING_LABELS[k]}</span>
                    <span className="text-white tabular-nums">{warningCounts[k] || 0}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPanelOpen(true)}
                className="mt-4 w-full text-xs font-mono uppercase tracking-wider border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-2"
                data-testid="radar-open-warnings-list-card"
              >
                Open full list →
              </button>
            </div>

            <div className="card-vg p-6">
              <div className="overline">Reflectivity Scale</div>
              <div className="mt-4 space-y-2 text-xs font-mono">
                <Bar from="#04e9e7" to="#04a4ff" label="5 – 25 dBZ · Light rain" />
                <Bar from="#04ff04" to="#fdf802" label="25 – 40 dBZ · Moderate" />
                <Bar from="#fdc400" to="#fd0000" label="40 – 55 dBZ · Heavy / hail" />
                <Bar from="#bd0000" to="#fd00fd" label="55 – 75 dBZ · Severe / large hail" />
              </div>
              <div className="mt-4 text-[10px] font-mono text-zinc-500">Last refresh: {lastUpdate || "—"}</div>
            </div>

            <div className="card-vg p-6">
              <div className="overline">Storm Reports · Last 12 hrs</div>
              <div className="font-heading text-4xl font-black mt-3 font-mono">{reports.length}</div>
              <ul className="mt-4 space-y-1 text-xs font-mono max-h-44 overflow-auto pr-1">
                {Object.entries(reportCounts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([k, v]) => (
                  <li key={k} className="flex items-center gap-3" data-testid={`report-count-${k}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: lsrColor(k) }} />
                    <span className="text-zinc-400 flex-1 uppercase tracking-wider truncate">{k}</span>
                    <span className="text-white tabular-nums">{v}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-[10px] font-mono text-zinc-500">Source: Iowa State Mesonet · NWS LSR</div>
            </div>
          </div>

          <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider text-center mt-10 max-w-2xl mx-auto leading-relaxed">
            Informational only. Click any warning polygon or storm marker for full details. For official watches and warnings consult the National Weather Service.
          </p>
        </div>
      </section>

      <style>{`
        .leaflet-popup-content-wrapper, .leaflet-popup-tip {
          background: #0d0f12 !important;
          color: #fff !important;
          border: 1px solid rgba(255,255,255,0.1) !important;
          border-radius: 0 !important;
          box-shadow: 0 8px 32px rgba(0,0,0,.45) !important;
        }
        .leaflet-popup-content { margin: 14px 16px !important; }
        .leaflet-tooltip.vg-tooltip {
          background: #0d0f12 !important;
          color: #fff !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          border-radius: 0 !important;
          box-shadow: 0 4px 16px rgba(0,0,0,.5) !important;
          font-family: 'JetBrains Mono', monospace !important;
          font-size: 11px !important;
        }
        .leaflet-tooltip.vg-tooltip::before { display: none !important; }
        .vg-tag-wrap { background: none; border: none; }
        .vg-tag-stack {
          transform: translate(-50%, -100%);
          display: flex;
          flex-direction: column;
          gap: 3px;
          align-items: center;
          white-space: nowrap;
        }
        .vg-tag {
          font-family: 'Share Tech Mono', 'JetBrains Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.12em;
          padding: 2px 6px;
          background: rgba(5, 11, 20, 0.92);
          border: 1px solid var(--tag-color, #00F0FF);
          color: #F4F6FF;
          text-shadow: 0 0 6px var(--tag-color, #00F0FF);
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.6);
        }
        .vg-tag-hail { border-color: #00F0FF; }
        .vg-tag-wind { border-color: #FF9900; }
        .leaflet-container a.leaflet-popup-close-button { color: #fff !important; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

function FilterChip({ active, onClick, color, children, dataTestid }) {
  return (
    <button
      onClick={onClick}
      data-testid={dataTestid}
      className={`px-3 py-1 text-[10px] font-mono uppercase tracking-wider border transition-colors`}
      style={{
        background: active ? `${color}26` : "transparent",
        color: active ? color : "#a1a1aa",
        borderColor: active ? `${color}99` : "rgba(255,255,255,0.1)",
      }}
    >
      {children}
    </button>
  );
}

function Bar({ from, to, label }) {
  return (
    <div>
      <div className="h-2 rounded-sm" style={{ background: `linear-gradient(90deg, ${from}, ${to})` }} />
      <div className="mt-1 text-zinc-400 text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}
